/**
 * Capitalizes the first letter of the given string.
 *
 * @param {string} string - The string to capitalize.
 * @returns {string} The capitalized string.
 * @example
 * capitalizeFirstLetter('andritz'); // returns 'Andritz'
 */
export function capitalizeFirstLetter(string: string): string {
  if (string.length === 0) return '';
  return string[0].toUpperCase() + string.slice(1);
}

/**
 * Checks if string `b` is included in string `a`, ignoring case.
 *
 * @param {string | null} a - The string to search within.
 * @param {string} b - The string to search for.
 * @returns {boolean} True if `b` is found in `a`, ignoring case.
 * @example
 * includesIgnoreCase('Hello World', 'hello'); // returns true
 * @example
 * includesIgnoreCase('Hello World', 'bye'); // returns false
 */
export const includesIgnoreCase = (a: string | null, b: string): boolean =>
  a?.toLowerCase().includes(b.toLowerCase()) ?? false;

/**
 * Truncates the given string to the specified limit and adds ellipsis if necessary.
 *
 * @param {string} string - The string to truncate.
 * @param {number} limit - The maximum length of the truncated string.
 * @returns {string} The truncated string.
 * @example
 * truncateString('Hello World', 5); // returns 'Hello...'
 * @example
 * truncateString('Hello', 10); // returns 'Hello'
 */
export const truncateString = (string: string, limit: number): string =>
  string.length <= limit ? string : `${string.substring(0, limit)}...`;

/**
 * Adds '/api' to the end of the given URL if it does not already end with '/api'.
 * Ensures the URL starts with 'https://'.
 *
 * @param {string} url - The URL to modify.
 * @returns {string} The modified URL.
 * @example
 * // returns 'https://example.com/api'
 * addApiToUrl('example.com');
 * @example
 * // returns 'https://example.com/api'
 * addApiToUrl('https://example.com');
 * @example
 * // returns 'https://example.com/api'
 * addApiToUrl('https://example.com/');
 */
export const addApiToUrl = (url: string): string => {
  let apiUrl = url.trim();
  if (!/^https?:\/\//i.test(apiUrl)) {
    apiUrl = 'https://' + apiUrl;
  }

  if (!apiUrl.endsWith('/api')) {
    apiUrl = apiUrl.replace(/\/?$/, '/api');
  }

  return apiUrl;
};

/**
 * Check if string is a valid URL
 *
 * @param {string} url - The string to validate
 * @returns {boolean} `true` if string a valid URL else `false`.
 *
 * @example
 * isValidUrl('https://example.com'); // true
 * isValidUrl('example.com'); // false
 * isValidUrl('http//example'); // false
 */
export const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Sanitizes a string by replacing non-alphanumeric characters with underscores,
 * reducing multiple consecutive underscores to a single underscore,
 * and trimming underscores from the beginning and end of the string.
 *
 * @param str - The string to sanitize
 * @returns The sanitized string
 *
 * @example
 * // Returns "Hello_World"
 * sanitizeString("Hello World!");
 *
 * @example
 * // Returns "user123"
 * sanitizeString("user@123");
 *
 * @example
 * // Returns "a_b_c"
 * sanitizeString("a--b__c");
 */
export const sanitizeString = (str: string) => {
  return str
    .replaceAll(/[^a-zA-Z0-9]/g, '_')
    .replaceAll(/_+/g, '_')
    .replaceAll(/(^_+)|(_+$)/g, '');
};

/**
 * Generates a string avatar (initials) from a user's name.
 *
 * @param name - The user's name.
 * @returns The generated string avatar (e.g., "JD" for "John Doe", "A" for "Alice").
 *
 * @example
 * stringAvatar('John Doe'); // returns 'JD'
 * stringAvatar('Alice Smith'); // returns 'AS'
 * stringAvatar('Alice'); // returns 'A'
 * stringAvatar(''); // returns ''
 */
export const stringAvatar = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`;
  }
  if (words.length === 1) {
    return words[0][0] || '';
  }
  return '';
};
